import React, { memo } from 'react';
import PropTypes from 'prop-types';

import { t } from 'i18next';

import { styled, LinearProgress, Typography } from '@datanomik/commons-ui';

import {
  Box,
  TablePagination,
} from '@mui/material';

import Grid from '../../../common/Grid';
import { Button } from '../../../common/Button';
import Switch from '../../../common/Switch';

import Notification from './Notification';

import useNotificationsList from './hooks';
import { styles } from './styles';
import { SkeletonNotification } from './Notification/Loader';

const NotificationsListRaw = memo(({
  className, type, handleClose, initialFilters,
}) => {
  const {
    loading,
    pageSize,
    isLoading,
    onlyUnRead,
    componentRef,
    pageNumber,
    totalElements,
    newNotifications,
    todayNotifications,
    historyNotifications,
    handleRead,
    handleView,
    handleReadAll,
    handleOnScroll,
    handleChangePage,
    handleChangeSwitch,
  } = useNotificationsList(type, handleClose, initialFilters);

  return (
    <Box className={`${className} dn-${type}`}>
      <Grid container>
        <Grid item xs={12}>
          <Box className="dn-flex-between dn-flex-middle dn-titles">
            <Box>
              <Typography className="dn-unread">
                {t('You have {{x}} new message/s!', {
                  x: newNotifications,
                })}
              </Typography>
            </Box>
            <Box className="dn-actions dn-flex-middle">
              {
                newNotifications > 0 && (
                  <Button variant="outlined" onClick={handleReadAll}>
                    {t('Mark all as read')}
                  </Button>
                )
              }
              {
                type === 'table' && (
                  <Box className="dn-flex-middle">
                    <Typography>
                      {t('Only show unread')}
                    </Typography>
                    <Switch
                      checked={onlyUnRead}
                      onChange={handleChangeSwitch}
                    />
                  </Box>
                )
              }
            </Box>
            <LinearProgress sx={{ opacity: loading ? 1 : 0, height: '2px' }} />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box ref={componentRef} onScroll={handleOnScroll} className="dn-notifications-all">
            <Grid container>
              {
                isLoading[[`${type}Loader`]] && (
                  <>
                    <Grid item xs={12}>
                      <SkeletonNotification maxRows={1} />
                    </Grid>
                    <Grid item xs={12}>
                      <SkeletonNotification maxRows={6} />
                    </Grid>
                  </>
                )
              }
              {
                todayNotifications.length > 0 && (
                  <Grid item xs={12}>
                    <Notification
                      title="Today"
                      notifications={todayNotifications}
                      withBack
                      readNotification={handleRead}
                      viewNotification={handleView}
                    />
                  </Grid>
                )
              }
              {
                historyNotifications.length > 0 && (
                  <Grid item xs={12}>
                    <Notification
                      title="History"
                      notifications={historyNotifications}
                      readNotification={handleRead}
                      viewNotification={handleView}
                    />
                  </Grid>
                )
              }
            </Grid>
          </Box>
        </Grid>
      </Grid>
      {
        type === 'table' && (
          <Grid item xs={12}>
            <Box className="dn-pagination dn-flex-right">
              <TablePagination
                component="div"
                count={totalElements}
                page={pageNumber}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
                onPageChange={handleChangePage}
                showFirstButton
                showLastButton
              />
            </Box>
          </Grid>
        )
      }
    </Box>
  );
});

NotificationsListRaw.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  handleClose: PropTypes.func,
  initialFilters: PropTypes.object,
};

const NotificationsList = styled(NotificationsListRaw)(styles);

NotificationsList.propTypes = NotificationsListRaw.propTypes;

export default memo(NotificationsList);
